import React, { PropsWithChildren, useMemo } from 'react'

import { Grid } from '@barracuda-internal/bds-core'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import { useFormatMessage } from 'lib/localization'
import { useBlockTransition } from 'lib/useBlockTransition'

import styles from 'components/libs/layout/pages/pagesLayoutStyles'
import ContentLoader from 'components/libs/contentLoader/ContentLoader'
import DomainConflictDialog from 'components/libs/domainConflictDialog/DomainConflictDialog'
import { useSettingsStateLogic } from 'components/libs/useSettingsState'

const DIALOG_I18N_KEY = 'ess.dialog'

export type SettingsPageContainerProps = {
  isLoading?: boolean
  isFailedToLoad?: boolean
  dirtyCheck?: boolean
  isDirtyForm?: boolean

  // using new prop instead of isLoading to prevent breaking pages
  // that may set the otherwise unused isLoading prop
  // Only porting modules to V2 will clean up this
  isContentLoading?: boolean
}

export const SettingsPageContainer = ({
  children,
  dirtyCheck = false,
  isDirtyForm = false,
  isContentLoading
}: PropsWithChildren<SettingsPageContainerProps>) => {
  const classes = styles()
  const formatDialogText = useFormatMessage(DIALOG_I18N_KEY)
  const [transitionCb, resetTransitionCb] = useBlockTransition({ dirtyCheck, isDirtyForm })
  const [UseSettingsStateLogic] = useSettingsStateLogic()

  return useMemo(
    () => (
      <ContentLoader
        isLoading={typeof isContentLoading !== 'undefined' ? isContentLoading : UseSettingsStateLogic.isLoading}
        isFailed={UseSettingsStateLogic.isFailedToLoad}
        fullPage
      >
        <Grid className={classes.pageContainer} alignItems="center" container direction="column">
          {children}
        </Grid>
        {!!transitionCb && (
          <WarningDialog
            title={formatDialogText('settings_page_container.title')}
            description={formatDialogText('settings_page_container.text')}
            onCancel={resetTransitionCb}
            onConfirm={transitionCb}
          />
        )}
        <DomainConflictDialog />
      </ContentLoader>
    ),
    [
      UseSettingsStateLogic,
      classes.pageContainer,
      children,
      transitionCb,
      formatDialogText,
      resetTransitionCb,
      isContentLoading
    ]
  )
}
